import { type ReactNode } from "react";

import { HamburgerIcon, CloseIcon, ChevronDownIcon } from "../icons/icons";
import {
  Box,
  Flex,
  Stack,
  Wrap,
  WrapItem,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  type BoxProps,
  type FlexProps,
  type StackProps,
  type WrapProps,
  type WrapItemProps,
  type InputProps,
  type MenuItemProps,
  type MenuButtonProps
} from "../internal/exports";
import { IconButton, type IconButtonProps } from "../button/icon-button";

export const HeaderLogo = ({
  className,
  children,
  order,
  id
}: {
  children: ReactNode;
  order?: BoxProps["order"];
  className?: string;
  id?: string;
}): JSX.Element => {
  return (
    <Box
      fontWeight={700}
      maxW={{ lg: "20%" }}
      cursor="pointer"
      order={order}
      className={className}
      id={id}
    >
      {children}
    </Box>
  );
};

export const HeaderMenu = ({
  className,
  children,
  open,
  id
}: Pick<StackProps, "children" | "className" | "id"> & {
  open: boolean;
}): JSX.Element => {
  return (
    <Box
      maxW={{ lg: "80%" }}
      flexBasis={{ base: "100%", lg: "auto" }}
      display={{ base: open ? "block" : "none", lg: "block" }}
    >
      <Stack
        gap={4}
        fontWeight={600}
        pt={{ base: 4, lg: 0 }}
        direction={{ base: "column", lg: "row" }}
        textAlign={{ base: "left", lg: "center" }}
        align={{ base: "flex-start", lg: "center" }}
        className={className}
        id={id}
      >
        {children}
      </Stack>
    </Box>
  );
};

export const HeaderMenuLinks = ({
  className,
  children,
  width,
  order,
  id
}: Pick<
  WrapProps,
  "children" | "order" | "className" | "id" | "width"
>): JSX.Element => {
  return (
    <Wrap
      gap={4}
      alignItems="center"
      justify="flex-end"
      direction={{ base: "column", lg: "row" }}
      id={id}
      width={width}
      order={order}
      className={className}
    >
      {children}
    </Wrap>
  );
};

export const HeaderMenuSubLink = ({
  className,
  children,
  id
}: Pick<MenuItemProps, "children" | "className" | "id">): JSX.Element => {
  return (
    <MenuItem whiteSpace="nowrap" id={id} className={className}>
      {children}
    </MenuItem>
  );
};

export const HeaderMenuSubLinks = ({
  className,
  children,
  title,
  id
}: {
  title: string;
} & Pick<MenuButtonProps, "children" | "className" | "id">): JSX.Element => {
  return (
    <WrapItem alignItems="center" id={id} className={className}>
      <Menu>
        <MenuButton
          textAlign="left"
          fontWeight={600}
          whiteSpace="nowrap"
          _focus={{ borderRadius: "sm", boxShadow: "outline" }}
        >
          {title}
          <ChevronDownIcon />
        </MenuButton>
        <MenuList color="initial">{children}</MenuList>
      </Menu>
    </WrapItem>
  );
};

export const HeaderMenuLink = ({
  className,
  children,
  id
}: Pick<WrapItemProps, "children" | "className" | "id">): JSX.Element => {
  return (
    <WrapItem
      alignItems="center"
      cursor="pointer"
      whiteSpace="nowrap"
      className={className}
      id={id}
    >
      {children}
    </WrapItem>
  );
};

export const HeaderSearch = ({
  placeholder,
  className,
  onChange,
  order,
  name,
  id,
  "aria-label": ariaLabel
}: Pick<
  InputProps,
  | "onChange"
  | "placeholder"
  | "aria-label"
  | "order"
  | "className"
  | "id"
  | "name"
>): JSX.Element => {
  return (
    <Input
      size={"sm" as "sm"}
      minW="150px"
      name={name}
      type="search"
      defaultValue=""
      autoComplete="off"
      _placeholder={{ color: "inherit" }}
      w={{ base: "100%", lg: "auto" }}
      placeholder={placeholder}
      aria-label={ariaLabel}
      className={className}
      onChange={onChange}
      order={order}
      id={id}
    />
  );
};

export const HeaderMenuToggle = ({
  className,
  onClick,
  open,
  icon,
  id,
  "aria-label": ariaLabel
}: Pick<IconButtonProps, "aria-label" | "onClick" | "className" | "id"> & {
  open: boolean;
  icon?: IconButtonProps["icon"];
}): JSX.Element => {
  return (
    <IconButton
      onClick={onClick}
      variant="outline"
      display={{ base: "block", lg: "none" }}
      icon={icon || open ? <CloseIcon /> : <HamburgerIcon />}
      aria-label={ariaLabel}
      className={className}
      id={id}
    />
  );
};

export const Header = ({
  className,
  id,
  boxShadow,
  background,
  fontSize,
  children,
  color
}: Pick<
  FlexProps,
  | "children"
  | "color"
  | "background"
  | "fontSize"
  | "boxShadow"
  | "className"
  | "id"
>): JSX.Element => {
  return (
    <Flex
      px={5}
      py={3}
      w="100%"
      as="nav"
      wrap="wrap"
      position="relative"
      alignItems="center"
      justifyContent="space-between"
      fontSize={fontSize || "sm"}
      boxShadow={boxShadow || "md"}
      background={background}
      color={color}
      className={className}
      id={id}
    >
      {children}
    </Flex>
  );
};

export default Header;
